import { VFC, ReactNode } from 'react'
import { SiteRoutes, matchSiteRoute, getAllowedSiteRoutes } from './siteRoutes'
import { useTranslation } from 'react-i18next'
import { Box, TabProps, Tab, Tabs, Tooltip } from '@mui/material'
import { NavLink, useParams } from 'react-router-dom'
import { RoutePaths } from '@/navigation/route'
import useAuth from '@/context/useAuth'

interface NavTabProps extends TabProps<typeof NavLink> {
    label: ReactNode
    to: string
}

// https://mui.com/material-ui/react-tabs/#nav-tabs
const NavTab: VFC<NavTabProps> = (props) => <Tab component={NavLink} {...props} />

const SiteNavLinks: VFC = () => {
    const { t } = useTranslation('site')
    const { siteHash } = useParams<{ siteHash: string }>()
    const { me } = useAuth()
    if (!me || !siteHash) {
        return null
    }
    const { userPermissions } = me
    const currentSite = me.sites?.find((site) => site.hash === siteHash)
    const isChurnedSite = Number(currentSite?.churned) === 1

    const allowedRoutes = getAllowedSiteRoutes(userPermissions, siteHash)
    const matchedRoute = matchSiteRoute(location.pathname)
    const selectedIndex = allowedRoutes.indexOf(matchedRoute || SiteRoutes.HOME)

    return (
        <Tabs orientation="vertical" value={selectedIndex} sx={{ mt: 2 }}>
            {allowedRoutes.map((href, index) => {
                const isConstraints = href === SiteRoutes.CONSTRAINTS
                const isDisabled = isConstraints && isChurnedSite
                const tab = (
                    <NavTab
                        key={href}
                        label={t(`navLinks.${href}`)}
                        to={`${RoutePaths.Sites}/${siteHash}/${href}`}
                        value={index}
                        disabled={isDisabled}
                    />
                )

                if (!isDisabled) {
                    return tab
                }

                return (
                    <Tooltip key={href} title={t('navLinksDisabled.constraintsChurned')} placement="right" arrow>
                        <Box sx={{ display: 'flex', width: '100%' }}>{tab}</Box>
                    </Tooltip>
                )
            })}
        </Tabs>
    )
}

export default SiteNavLinks
